// Diálogo para resolver un conflicto entre lo que registró una persona y lo
// que reportó la máquina (contrato de ingesta §8, desviación 18).
//
//   - "Mantener lo de la persona"  → accion: mantener_persona (siempre posible)
//   - "Aplicar lo de la máquina"   → accion: aplicar_maquina (solo si `aplicable`;
//     existe para cantidad y lote: la fila vuelve a control de la máquina)
//
// La razón es opcional y queda en la auditoría. Presentacional: la página
// maneja la mutation y entrega el error del servidor ya legible.

import { useState } from 'react';
import type { AccionConflictoIngesta, ConflictoIngesta, ConflictoResolver } from 'schemas';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { cn } from '../../../lib/utils';
import { etiquetaCampo, etiquetaRazon, valorLegible } from './etiquetas';

export interface ConflictoResolverDialogProps {
  conflicto: ConflictoIngesta;
  guardando: boolean;
  serverError: string | null;
  onResolver: (body: ConflictoResolver) => void;
  onClose: () => void;
}

export function ConflictoResolverDialog({
  conflicto: c,
  guardando,
  serverError,
  onResolver,
  onClose,
}: ConflictoResolverDialogProps) {
  const [accion, setAccion] = useState<AccionConflictoIngesta>('mantener_persona');
  const [razon, setRazon] = useState('');

  function confirmar() {
    const r = razon.trim();
    onResolver({ accion, ...(r ? { razon: r.slice(0, 500) } : {}) });
  }

  return (
    <Dialog open onClose={onClose} className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Resolver conflicto</DialogTitle>
        <DialogDescription>
          {c.linea_nombre}
          {c.fecha ? ` · ${c.fecha}` : ''}
          {c.hora_inicio_bloque ? ` · ${c.hora_inicio_bloque.slice(0, 5)}` : ''} ·{' '}
          {etiquetaRazon(c.razon)}
        </DialogDescription>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-2">
          <OpcionValor
            titulo="Lo de la persona"
            campo={etiquetaCampo(c.campo)}
            valor={valorLegible(c.valor_actual)}
            seleccionada={accion === 'mantener_persona'}
            onSelect={() => setAccion('mantener_persona')}
            accionLabel="Mantener lo de la persona"
          />
          <OpcionValor
            titulo="Lo de la máquina"
            campo={etiquetaCampo(c.campo)}
            valor={valorLegible(c.valor_maquina)}
            seleccionada={accion === 'aplicar_maquina'}
            disabled={!c.aplicable}
            onSelect={() => setAccion('aplicar_maquina')}
            accionLabel="Aplicar lo de la máquina"
          />
        </div>
        {!c.aplicable ? (
          <p className="text-xs text-fg-tertiary">
            Para este tipo de conflicto solo se puede mantener el valor de la persona.
          </p>
        ) : accion === 'aplicar_maquina' && c.campo === 'cantidad_procesada' ? (
          <p className="rounded border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-warn">
            La hora vuelve a control de la máquina y se recalcula con sus conteos vigentes. Los
            demás conflictos de cantidad de esa hora quedan resueltos con esta decisión.
          </p>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="conflicto-razon">Razón (opcional)</Label>
          <Textarea
            id="conflicto-razon"
            value={razon}
            maxLength={500}
            onChange={(e) => setRazon(e.target.value)}
            placeholder="Queda registrada en la auditoría"
          />
        </div>
        {serverError ? (
          <p role="alert" className="rounded bg-crit-soft px-3 py-2 text-xs text-crit">
            {serverError}
          </p>
        ) : null}
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={guardando}>
          Cancelar
        </Button>
        <Button onClick={confirmar} loading={guardando}>
          {accion === 'aplicar_maquina' ? 'Aplicar lo de la máquina' : 'Mantener lo de la persona'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function OpcionValor({
  titulo,
  campo,
  valor,
  seleccionada,
  disabled = false,
  onSelect,
  accionLabel,
}: {
  titulo: string;
  campo: string;
  valor: string;
  seleccionada: boolean;
  disabled?: boolean;
  onSelect: () => void;
  accionLabel: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={seleccionada}
      aria-label={accionLabel}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex flex-col items-start gap-1 rounded border px-3 py-2.5 text-left transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        seleccionada
          ? 'border-accent bg-accent-soft'
          : 'border-border bg-surface-elevated hover:border-border-strong',
      )}
    >
      <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        {titulo}
      </span>
      <span className="break-all text-lg font-medium leading-tight tabular-nums text-fg">
        {valor}
      </span>
      <span className="text-[11px] text-fg-tertiary">{campo}</span>
    </button>
  );
}
